import { toast } from 'react-toastify';
// Assuming these are defined in your configuration file
import { BASE_URL, AUTH_PREFIX, REFRESH_URL } from './config';

// ---------------------------------------------------------
// 🔐 Access Token (in-memory only)
// ---------------------------------------------------------
let accessToken = null;
let isRefreshing = false;
let refreshPromise = null;

export const setAccessToken = (t) => {
    console.log(`🔐 [API] setAccessToken called. Token exists? ${!!t}`);
    accessToken = t || null;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
    console.log("🗑️ [API] clearAccessToken called. Token wiped.");
    accessToken = null;
};

// ---------------------------------------------------------
// ♻️ Refresh Access Token (Centralized Logic)
// ---------------------------------------------------------
export const refreshAccessToken = async () => {
    if (isRefreshing) {
        console.log("♻️ [API] Refresh already in progress... returning existing promise.");
        return refreshPromise;
    }

    console.log("♻️ [API] Starting Token Refresh Sequence...");
    isRefreshing = true;

    refreshPromise = fetch(REFRESH_URL, { method: 'POST', credentials: 'include' })
        .then(async (res) => {
            console.log(`⬅️ [API] Refresh Response: ${res.status}`);
            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data?.accessToken) {
                console.error("❌ [API] Refresh Failed:", data);
                throw new Error('Token refresh failed');
            }

            console.log("✅ [API] Refresh Success! New Access Token received.");
            setAccessToken(data.accessToken);
            // Return token data for AuthContext to update session
            return { accessToken: data.accessToken, accessTokenExpires: data.accessTokenExpires };
        })
        .catch((err) => {
            console.warn('⚠️ [API] Token refresh caught error:', err.message);
            throw err; // Crucial to throw to signal failure to the caller
        })
        .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
        });

    return refreshPromise;
};

// ---------------------------------------------------------
// 🚪 Auto Logout (Client-side fail-safe)
// ---------------------------------------------------------
const autoLogout = () => {
    console.warn("🚪 [API] Auto Logout Triggered (Session Expired)");
    clearAccessToken();
    const current = window.location.pathname;
    if (!['/login', '/register'].includes(current))
        localStorage.setItem('postLoginRedirect', current);

    toast.error('Session expired. Please log in again.', { icon: '💬 talkear' });
    setTimeout(() => (window.location.href = '/login'), 800);
};

// ---------------------------------------------------------
// 📡 Core Fetch Logic Helper
// ---------------------------------------------------------
const buildFetch = (url, options = {}, tokenOverride = getAccessToken()) => {
    const finalUrl = /^https?:\/\//i.test(url) ? url : `${BASE_URL}${url}`;
    const { skipAuthRefresh, ...fetchOpts } = options || {};

    // 🔍 DEBUG: Log the Outgoing Request
    console.log(`🚀 [API] Request: ${fetchOpts.method || 'GET'} ${url}`, {
        hasToken: !!tokenOverride,
        skipAuthRefresh: !!skipAuthRefresh
    });

    const headers = {
        ...(fetchOpts.headers || {}),
        // Include Authorization header only if a token is explicitly passed or available
        ...(tokenOverride ? { Authorization: `Bearer ${tokenOverride}` } : {}),
    };

    return fetch(finalUrl, {
        ...fetchOpts,
        headers,
        credentials: 'include',
    });
};

// ---------------------------------------------------------
// 🛡️ 1. Authenticated Fetch Wrapper (STRICT AUTH)
// ---------------------------------------------------------
export const authFetch = async (url, options = {}) => {
    const { skipAuthRefresh } = options || {};

    try {
        let res = await buildFetch(url, options);
        console.log(`⬅️ [API] Response (Strict): ${res.status} for ${url}`);

        if (skipAuthRefresh) return res;

        // Check for 401 Unauthorized
        if (res.status === 401) {
            console.warn('🔁 [API] 401 Detected — Attempting Refresh...');

            try {
                // Attempt to refresh the token
                await refreshAccessToken();

                // Retry the original request with the new token
                console.log("🔁 [API] Retrying original request with new token...");
                res = await buildFetch(url, options);
                console.log(`⬅️ [API] Retry Response: ${res.status}`);

                if (res.status === 401) {
                    // This means the session is broken even after refresh
                    console.error("❌ [API] Still 401 after refresh. Forcing Logout.");
                    autoLogout();
                    throw new Error('Unauthorized even after refresh');
                }
            } catch (err) {
                // Catch the error thrown by refreshAccessToken() on failure
                console.error("❌ [API] Refresh failed completely. Forcing Logout.");
                autoLogout();
                throw new Error('Unauthorized after refresh failure');
            }
        }
        return res;
    } catch (err) {
        console.error('💥 [authFetch] Network/Logic Error:', err);
        throw err;
    }
};

// ---------------------------------------------------------
// 🤝 2. Soft Auth Fetch Wrapper (SOFT AUTH)
// ---------------------------------------------------------
export const softAuthFetch = async (url, options = {}) => {
    try {
        // Send token if available, but do not attempt refresh
        const res = await buildFetch(url, options);
        console.log(`⬅️ [API] Response (Soft): ${res.status} for ${url}`);

        if (res.status === 401) {
            console.warn('🟠 [softAuthFetch] Token expired (401). Continuing as unauthenticated.');
        }

        return res;
    } catch (err) {
        console.error('💥 [softAuthFetch] Error:', err);
        throw err;
    }
};

// ---------------------------------------------------------
// 🔓 3. Unauthenticated Fetch Wrapper (NO AUTH)
// ---------------------------------------------------------
export const unAuthFetch = async (url, options = {}) => {
    try {
        // Explicitly pass null for the token to ensure NO token is sent.
        const res = await buildFetch(url, options, null);
        console.log(`⬅️ [API] Response (Unauth): ${res.status} for ${url}`);
        return res;
    } catch (err) {
        console.error('💥 [unAuthFetch] Error:', err);
        throw err;
    }
};


// ---------------------------------------------------------
// 🧩 Common HTTP Helpers (Strict Auth)
// ---------------------------------------------------------
export const authGet = (url, opts = {}) =>
    authFetch(url, { method: 'GET', ...opts });

export const authPost = (url, data, opts = {}) =>
    authFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        ...opts,
    });

export const authPut = (url, data, opts = {}) =>
    authFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        ...opts,
    });

export const authDelete = (url, opts = {}) =>
    authFetch(url, { method: 'DELETE', ...opts });

export const authUpload = (url, formData, opts = {}) =>
    authFetch(url, { method: 'POST', body: formData, ...opts });


// ---------------------------------------------------------
// 🤝 Common HTTP Helpers (Soft Auth)
// ---------------------------------------------------------
export const softGet = (url, opts = {}) =>
    softAuthFetch(url, { method: 'GET', ...opts });

export const softPost = (url, data, opts = {}) =>
    softAuthFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        ...opts,
    });


// ---------------------------------------------------------
// 🔓 Common HTTP Helpers (Unauth)
// ---------------------------------------------------------
export const unAuthGet = (url, opts = {}) =>
    unAuthFetch(url, { method: 'GET', ...opts });

export const unAuthPost = (url, data, opts = {}) =>
    unAuthFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        ...opts,
    });

// ---------------------------------------------------------
// 🔐 Dedicated helpers for auth endpoints (unchanged)
// ---------------------------------------------------------
export const loginRequest = (email, password) =>
    authPost(`${AUTH_PREFIX}/login`, { email, password }, { skipAuthRefresh: true });

export const registerRequest = (payload) =>
    authPost(`${AUTH_PREFIX}/register`, payload, { skipAuthRefresh: true });